/**
 * Lot 2bis (Franck, 15/10/2026) : le signataire a demandé une transmission
 * ou la correction de son nom ; l'émetteur doit répondre. L'écran se met à
 * jour seul (toutes les 5 s) ; un e-mail part aussi à la décision.
 *
 * La route de la demande répond même quand le lien a expiré : une
 * transmission acceptée remplace ce lien, et l'écran doit pouvoir dire
 * « transmise » au lieu de « lien expiré ».
 */
import { useEffect, useState } from "react";

import { fetchDemande, type Demande } from "../api";
import { Ico } from "../components/Icone";
import type { t } from "../i18n";

interface Props {
  token: string;
  L: ReturnType<typeof t>;
  client: string;
  demande: Demande;
  /** La décision est tombée et le parcours reprend (nom accepté, ou refus lu). */
  onReprendre: () => void;
}

function personne(d: Demande): { nom: string; email: string } {
  return typeof d.nouveau === "string" ? { nom: d.nouveau, email: "" } : d.nouveau;
}

export function EnAttente({ token, L, client, demande, onReprendre }: Props) {
  const [d, setD] = useState(demande);

  useEffect(() => {
    if (d.statut !== "en_attente") return;
    const id = window.setInterval(() => {
      fetchDemande(token).then((n) => n && setD(n), () => undefined);
    }, 5000);
    return () => window.clearInterval(id);
  }, [token, d.statut]);

  // Nom accepté : la ligne porte le nom vérifié, le parcours reprend seul.
  useEffect(() => {
    if (d.statut === "acceptee" && d.type === "nom") onReprendre();
  }, [d.statut, d.type, onReprendre]);

  const p = personne(d);
  const resume = d.type === "transmission" ? L.attenteTransmission(p.nom, p.email) : L.attenteNom(p.nom);

  if (d.statut === "acceptee" && d.type === "transmission") {
    return (
      <section className="card stack carte-etroite attente attente--fait" aria-live="polite">
        <span className="attente__signe"><Ico nom="envoi" taille={24} /></span>
        <h1 className="card__title">{L.transmisTitre}</h1>
        <p className="card__lead">{L.transmisCorps(p.nom, p.email)}</p>
      </section>
    );
  }

  if (d.statut === "refusee") {
    return (
      <section className="card stack carte-etroite attente attente--refus" aria-live="polite">
        <span className="attente__signe"><Ico nom="croix" taille={24} /></span>
        <h1 className="card__title">{L.attenteRefuseeTitre}</h1>
        <p className="card__lead">
          {d.type === "transmission" ? L.attenteRefuseeTransmission(client) : L.attenteRefuseeNom(client)}
        </p>
        <p className="attente__resume">{resume}</p>
        <button type="button" className="btn btn--primary btn--bloc" onClick={onReprendre}>
          {d.type === "transmission" ? L.retourAuDocument : L.continuer}
        </button>
      </section>
    );
  }

  return (
    <section className="card stack carte-etroite attente" aria-live="polite">
      <span className="attente__signe"><Ico nom="horloge" taille={24} /></span>
      <h1 className="card__title">{L.attenteTitre(client)}</h1>
      <p className="attente__resume">{resume}</p>
      <p className="attente__note">{L.attenteNote}</p>
    </section>
  );
}
